import { useCallback, useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { FormikConfig, FormikHelpers } from 'formik';
import { createContext, useContext } from 'use-context-selector';
import * as Yup from 'yup';

import { useAuth } from '~providers';

export type LoginValues = {
  email: string;
  password: string;
  submit: unknown;
};

export const loginValidationSchema = Yup.object({
  email: Yup.string().max(255).required('Email is required'),
  password: Yup.string().max(255).required('Password is required'),
});

const initialContextValues = {
  loginValues: {} as LoginValues,
  handleSubmit: (
    _: LoginValues,
    _formikHelpers: FormikHelpers<LoginValues>,
  ) => {
    return;
  },
  isLoggingIn: false,
};

const LoginContext = createContext(initialContextValues);

type Props = {
  children: React.ReactNode;
};

export const LoginProvider: React.FC<Props> = ({ children }) => {
  // Initial data
  const initialLoginValues = useMemo(
    () => ({
      email: '',
      password: '',
      submit: null,
    }),
    [],
  );

  const { signIn } = useAuth();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleSubmit: FormikConfig<LoginValues>['onSubmit'] = useCallback(
    async (values, helpers) => {
      try {
        setIsLoggingIn(true);
        await signIn(values);
      } catch (err) {
        if (err instanceof Yup.ValidationError || isAxiosError(err)) {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: 'Invalid email or password.' });
          helpers.setSubmitting(false);
        }
      } finally {
        setIsLoggingIn(false);
      }
    },
    [signIn],
  );

  const providerValues = useMemo(
    () => ({
      loginValues: initialLoginValues,
      handleSubmit,
      isLoggingIn,
    }),
    [handleSubmit, initialLoginValues, isLoggingIn],
  );

  return (
    <LoginContext.Provider value={providerValues}>
      {children}
    </LoginContext.Provider>
  );
};

export const useLoginContext = () => useContext(LoginContext);
